import React, { useEffect, useState } from "react";
import {
  Alert,
  Row,
  Col,
  Form,
  Label,
  Button,
  UncontrolledTooltip,
} from "reactstrap";

//Social Media Imports
import { GoogleLogin } from "react-google-login";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

// router
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

// validations
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

// config
import config from "../../config";

// hooks
import { useProfile, useRedux } from "../../hooks/index";
import { createSelector } from "reselect";
//actions
import { loginUser, socialLogin } from "../../redux/actions";

// components
import NonAuthLayoutWrapper from "../../components/NonAutnLayoutWrapper";
import AuthHeader from "../../components/AuthHeader";
import FormInput from "../../components/FormInput";
import Loader from "../../components/Loader";

// services
import { loginUserAccount } from "../../services/auth";
import { generateJWT } from "../../api/auth";

// utils
import { redirectAfterDelay } from "../../utils/redirectAfterDelay";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useTranslation } from "react-i18next";

const auth = getAuth();

interface LoginProps {}
interface LoginFormValues {
  email: string;
  password: string;
}

interface JwtTokenType {
  token: string;
}
const Login = (props: LoginProps) => {
  const { t, i18n } = useTranslation();
  const handleLogoClick = () => {
    navigate("/landing-page");
  };
  // global store
  const { dispatch, useAppSelector } = useRedux();

  const errorData = createSelector(
    (state: any) => state.Login,
    state => ({
      isUserLogin: state.isUserLogin,
      error: state.error,
      loginLoading: state.loading,
      isUserLogout: state.isUserLogout,
    }),
  );
  // Inside your component
  const { isUserLogin, error, loginLoading, isUserLogout } =
    useAppSelector(errorData);

  const navigate = useNavigate();
  const location = useLocation();
  const [redirectUrl, setRedirectUrl] = useState("/dashboard");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const url =
      location.state && location.state.from
        ? location.state.from.pathname
        : "/";
    setRedirectUrl(url);
  }, [location]);
  useEffect(() => {
    if (isUserLogin && !loginLoading && !isUserLogout) {
      navigate(redirectUrl);
    }
  }, [isUserLogin, navigate, loginLoading, isUserLogout, redirectUrl]);

  const resolver = yupResolver(
    yup.object().shape({
      email: yup.string().required("Please Enter E-mail."),
      password: yup.string().required("Please Enter Password."),
    }),
  );

  const methods = useForm({ resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = methods;

  const handleRedirect = () => {
    redirectAfterDelay(navigate, "/dashboard", 1000); // Redirects to "/target-page" after 3 seconds
  };

  const onSubmitForm = async (values: LoginFormValues) => {
    setIsSubmitting(true);
    try {
      const response = await loginUserAccount(values.email, values.password);

      if (response.success) {
        setAlertMessage("Login successful!");
        setAlertType("success");

        const authUser = response?.data;
        const userData = { ...authUser, userId: response?.userId };

        const payload = { userId: response?.userId };
        generateJWT(payload).then(jwtResponse => {
          const jwtToken = jwtResponse?.token;

          localStorage.setItem("token", jwtToken);

          // Redirect to the dashboard or the intended route
          handleRedirect();
        });
      } else {
        if (response.message == "Firebase: Error (auth/invalid-credential).") {
          setAlertMessage("Invalid User Credential");
        } else {
          setAlertMessage(
            response.message || "Invalid email or password. Please try again.",
          );
        }

        setAlertType("danger");
      }
    } catch (error) {
      setAlertMessage(
        "An error occurred while logging in. Please try again later.",
      );
      setAlertType("danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { userProfile, loading } = useProfile();

  if (userProfile && !loading) {
    return <Navigate to={{ pathname: redirectUrl }} />;
  }

  const signIn = (res: any, type: "google" | "facebook") => {
    if (type === "google" && res) {
      const postData = {
        name: res.profileObj.name,
        email: res.profileObj.email,
        token: res.tokenObj.access_token,
        idToken: res.tokenId,
      };
      dispatch(socialLogin(postData, type));
    } else if (type === "facebook" && res) {
      const postData = {
        name: res.name,
        token: res.accessToken,
      };
      dispatch(socialLogin(postData, type));
    }
  };

  //handleFacebookLoginResponse
  const facebookResponse = (response: object) => {
    signIn(response, "facebook");
  };

  //handleGoogleLoginResponse
  const googleResponse = (response: object) => {
    signIn(response, "google");
  };

  return (
    <NonAuthLayoutWrapper>
      <Row className=" justify-content-center my-auto">
        <Col sm={8} lg={6} xl={5} className="col-xxl-4">
          <div className="py-md-5 py-4">
            <AuthHeader
              title={t("Welcome Back !")}
              subtitle={t("Sign in to continue to Invoice.")}
            />

            {alertMessage && (
              <Alert color={alertType} toggle={() => setAlertMessage("")}>
                {alertMessage}
              </Alert>
            )}

            <Form
              onSubmit={handleSubmit(onSubmitForm)}
              className="position-relative"
            >
              {loginLoading && <Loader />}
              <div className="mb-3">
                <FormInput
                  label={t("Email Address")}
                  type="text"
                  name="email"
                  register={register}
                  errors={errors}
                  control={control}
                  labelClassName="form-label"
                  placeholder={t("Enter email")}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <FormInput
                  label={t("Password")}
                  type="password"
                  name="password"
                  register={register}
                  errors={errors}
                  control={control}
                  labelClassName="form-label"
                  className="form-control pe-5"
                  placeholder={t("Enter Password")}
                />
              </div>

              <div className="form-check form-check-info font-size-16">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="remember-check"
                />
                <Label
                  className="form-check-label font-size-14"
                  htmlFor="remember-check"
                >
                  {t("Remember me")}
                </Label>
              </div>

              <div className="text-center mt-4">
                <Button
                  color="primary"
                  className="w-100"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("Processing...") : t("Log In")}
                </Button>
              </div>
            </Form>

            <div className="mt-5 text-center text-muted">
              <p>
                {t("Don't have an account ?")}
                <Link
                  to="/auth-register"
                  className="fw-medium text-decoration-underline"
                >
                  {" "}
                  {t("Register")}
                </Link>
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </NonAuthLayoutWrapper>
  );
};

export default Login;
